import * as React from 'react'
import {
  FaIcon,
  faXmark,
  faArrowRight,
  faArrowLeft,
  faChevronUp,
  faChevronDown,
} from '@/lib/fa'
import { Button } from '@/ui/button'
import { Portal } from '@/ui/overlays/Portal'
import { toast } from '@/lib/toasts/api'
import { ReportType } from '@/app/constants/reportType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { getReportOptionLabelKey } from './PrintModal'
import {
  TimesheetPrintoutConfiguration,
  useSaveTimesheetPrintoutConfiguration,
} from '../hooks/useTimesheetPrintoutConfiguration'

interface Props {
  companyId: string
  configuration: TimesheetPrintoutConfiguration
  onClose: () => void
}

type ListProps = {
  title: string
  items: ReportType[]
  selected: ReportType | null
  onSelect: (type: ReportType) => void
  onDoubleClick: (type: ReportType) => void
  label: (type: ReportType) => string
  numbered?: boolean
}

const ReportList: React.FC<ListProps> = ({
  title,
  items,
  selected,
  onSelect,
  onDoubleClick,
  label,
  numbered,
}) => (
  <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-md tw-border tw-border-[#e5e6e6]">
    <div className="tw-flex tw-h-8 tw-shrink-0 tw-items-center tw-border-b tw-border-[#e5e6e6] tw-bg-[#f2f2f2] tw-bg-opacity-25 tw-px-3">
      <span className="tw-text-xs tw-font-medium tw-text-[#1f2937]">{title}</span>
    </div>
    <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto" role="listbox">
      {items.map((type, index) => (
        <div
          key={type}
          role="option"
          aria-selected={selected === type}
          onClick={() => onSelect(type)}
          onDoubleClick={() => onDoubleClick(type)}
          className={`tw-flex tw-h-9 tw-cursor-pointer tw-select-none tw-items-center tw-gap-2 tw-border-b tw-border-[#e5e6e6] tw-px-3 tw-text-xs tw-text-[#1f2937] ${
            selected === type ? 'tw-bg-[#e8edfb] tw-font-medium' : 'hover:tw-bg-gray-50'
          }`}
        >
          {numbered && (
            <span className="tw-w-4 tw-shrink-0 tw-text-[#2448bb]">{index + 1}.</span>
          )}
          <span className="tw-truncate" title={label(type)}>
            {label(type)}
          </span>
        </div>
      ))}
    </div>
  </div>
)

const IconButton: React.FC<{
  icon: typeof faArrowRight
  label: string
  disabled?: boolean
  onClick: () => void
}> = ({ icon, label, disabled, onClick }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    disabled={disabled}
    onClick={onClick}
    className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-[#e5e6e6] tw-bg-white hover:tw-bg-gray-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-40"
  >
    <FaIcon icon={icon} className="tw-h-3.5 tw-w-3.5 tw-text-[#2448bb]" />
  </button>
)

/** Odpowiednik WPF TimesheetPrintoutConfigurationView: dostępne raporty → wyświetlane (maks. 4). */
export const TimesheetPrintoutConfigurationModal: React.FC<Props> = ({
  companyId,
  configuration,
  onClose,
}) => {
  const { t } = useLocalize()
  const save = useSaveTimesheetPrintoutConfiguration(companyId)
  const [displayed, setDisplayed] = React.useState<ReportType[]>(
    configuration.reportTypes,
  )
  const [selectedAvailable, setSelectedAvailable] =
    React.useState<ReportType | null>(null)
  const [selectedDisplayed, setSelectedDisplayed] =
    React.useState<ReportType | null>(null)

  const label = (type: ReportType) => {
    const key = getReportOptionLabelKey(type)
    return key ? t(key) : type
  }

  const available = configuration.availableReportTypes.filter(
    (type) => !displayed.includes(type),
  )

  const add = (type: ReportType | null) => {
    if (!type) return
    if (displayed.length >= configuration.maxReports) {
      toast.warning({
        text: t('printout.too-many-reports', {
          count: configuration.maxReports,
        }),
      })
      return
    }
    setDisplayed((prev) => [...prev, type])
    setSelectedAvailable(null)
  }

  const remove = (type: ReportType | null) => {
    if (!type) return
    setDisplayed((prev) => prev.filter((item) => item !== type))
    setSelectedDisplayed(null)
  }

  const move = (offset: -1 | 1) => {
    if (!selectedDisplayed) return
    setDisplayed((prev) => {
      const index = prev.indexOf(selectedDisplayed)
      const target = index + offset
      if (index < 0 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      next[index] = next[target]
      next[target] = selectedDisplayed
      return next
    })
  }

  const selectedIndex = selectedDisplayed
    ? displayed.indexOf(selectedDisplayed)
    : -1

  const handleSave = async () => {
    try {
      await save.mutateAsync(displayed)
      toast.success({ text: t('printout.configuration-saved') })
      onClose()
    } catch (error) {
      toast.error({
        text: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <Portal layer="modal">
      <div className="tw-fixed tw-inset-0 tw-z-[200] tw-flex tw-items-center tw-justify-center tw-bg-[#1f2937] tw-bg-opacity-70 tw-px-4">
        <div className="tw-flex tw-max-h-[calc(100vh-32px)] tw-w-[760px] tw-max-w-full tw-flex-col tw-overflow-hidden tw-rounded-lg tw-bg-white">
          <div className="tw-flex tw-h-[64px] tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-px-6">
            <h2 className="tw-text-xl tw-font-semibold tw-text-[#1f2937]">
              {t('printout.timesheet-printout-configuration')}
            </h2>
            <button
              onClick={onClose}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded hover:tw-bg-gray-100"
              aria-label="Close"
            >
              <FaIcon icon={faXmark} className="tw-h-5 tw-w-5 tw-text-[#1f2937]" />
            </button>
          </div>

          <div className="tw-flex tw-h-[420px] tw-min-h-0 tw-gap-3 tw-p-6">
            <ReportList
              title={t('printout.available-reports')}
              items={available}
              selected={selectedAvailable}
              onSelect={setSelectedAvailable}
              onDoubleClick={add}
              label={label}
            />

            <div className="tw-flex tw-shrink-0 tw-flex-col tw-justify-center tw-gap-2">
              <IconButton
                icon={faArrowRight}
                label={t('printout.add-to-displayed')}
                disabled={!selectedAvailable}
                onClick={() => add(selectedAvailable)}
              />
              <IconButton
                icon={faArrowLeft}
                label={t('printout.remove-from-displayed')}
                disabled={!selectedDisplayed}
                onClick={() => remove(selectedDisplayed)}
              />
            </div>

            <ReportList
              title={`${t('printout.displayed-reports')} (${displayed.length}/${configuration.maxReports})`}
              items={displayed}
              selected={selectedDisplayed}
              onSelect={setSelectedDisplayed}
              onDoubleClick={remove}
              label={label}
              numbered
            />

            <div className="tw-flex tw-shrink-0 tw-flex-col tw-justify-center tw-gap-2">
              <IconButton
                icon={faChevronUp}
                label={t('printout.move-up')}
                disabled={selectedIndex <= 0}
                onClick={() => move(-1)}
              />
              <IconButton
                icon={faChevronDown}
                label={t('printout.move-down')}
                disabled={selectedIndex < 0 || selectedIndex >= displayed.length - 1}
                onClick={() => move(1)}
              />
            </div>
          </div>

          <div className="tw-flex tw-h-[70px] tw-shrink-0 tw-items-center tw-justify-end tw-gap-3 tw-border-t tw-border-[#e5e6e6] tw-px-6">
            <Button variant="neutral" mode="outline" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              loading={save.isPending}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  )
}
